import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Cog6ToothIcon, XMarkIcon, Bars3Icon, ChevronUpIcon, ChevronDownIcon, BuildingOfficeIcon, ArrowUpTrayIcon } from '@heroicons/react/24/outline';
import Layout from './Layout';
import api, { userAPI } from '../services/api';
import { Lead, LeadStage, LeadSource, User } from '../types/Lead';
import { CustomerSegment } from '../types/CustomerSegment';
import { customerSegmentApi } from '../services/customerSegmentApi';
import { formatDateForDisplay } from '../utils/dateUtils';
import { useBranch } from '../contexts/BranchContext';
import PermissionGate from './PermissionGate';
import BulkLeadImportModal from './BulkLeadImportModal';

interface Pagination {
  currentPage: number;
  totalPages: number;
  totalRecords: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
}

interface ColumnConfig {
  id: string;
  label: string;
  visible: boolean;
  order: number;
}

const LEADS_LIST_SCROLL_KEY = 'leadListScrollY';
const LEADS_LIST_RETURN_LEAD_ID_KEY = 'leadListReturnLeadId';
const LEADS_LIST_PAGE_KEY = 'leadListPage';

const Leads: React.FC = () => {
  const navigate = useNavigate();
  const { currentBranch, isViewingAllBranches, loading: branchLoading } = useBranch();
  const [leads, setLeads] = useState<Lead[]>([]);
  const [stages, setStages] = useState<LeadStage[]>([]);
  const [sources, setSources] = useState<LeadSource[]>([]);
  const [segments, setSegments] = useState<CustomerSegment[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showColumnSettings, setShowColumnSettings] = useState(false);
  const [draggedColumn, setDraggedColumn] = useState<string | null>(null);
  const [dragOverColumn, setDragOverColumn] = useState<string | null>(null);
  const [savingColumnSettings, setSavingColumnSettings] = useState(false);
  const [showBulkImportModal, setShowBulkImportModal] = useState(false);
  const [highlightedLeadId, setHighlightedLeadId] = useState<number | null>(null);
  const [pagination, setPagination] = useState<Pagination>({
    currentPage: 1,
    totalPages: 1,
    totalRecords: 0,
    hasNextPage: false,
    hasPrevPage: false
  });

  // Default column configuration
  const [columns, setColumns] = useState<ColumnConfig[]>([
    { id: 'leadUid', label: 'Lead ID', visible: true, order: 1 },
    { id: 'name', label: 'Name', visible: true, order: 2 },
    { id: 'contact', label: 'Contact', visible: true, order: 3 },
    { id: 'company', label: 'Company', visible: false, order: 4 },
    { id: 'segments', label: 'Segments', visible: true, order: 5 },  // NEW
    { id: 'stage', label: 'Stage', visible: true, order: 6 },
    { id: 'source', label: 'Source', visible: true, order: 7 },
    { id: 'assignedTo', label: 'Assigned To', visible: true, order: 8 },
    { id: 'leadOwner', label: 'Lead Owner', visible: false, order: 9 },
    { id: 'priority', label: 'Priority', visible: true, order: 10 },
    { id: 'created', label: 'Created', visible: true, order: 11 },
    // Additional columns that can be enabled
    { id: 'email', label: 'Email', visible: false, order: 12 },
    { id: 'phone', label: 'Phone', visible: false, order: 13 },
    { id: 'leadValue', label: 'Lead Value', visible: false, order: 14 },
    { id: 'followUpDate', label: 'Follow-up Date', visible: false, order: 15 },
    { id: 'lastActivity', label: 'Last Activity', visible: false, order: 16 },
    { id: 'notes', label: 'Notes', visible: false, order: 17 }
  ]);

  // Column management functions
  const toggleColumnVisibility = async (columnId: string) => {
    const newColumns = columns.map(col => 
      col.id === columnId ? { ...col, visible: !col.visible } : col
    );
    setColumns(newColumns);
    await saveColumnSettings(newColumns);
  };


  const resetToDefaultColumns = async () => {
      const defaultColumns = columns.map(col => ({
      ...col,
      visible: ['leadUid', 'name', 'contact', 'segments', 'stage', 'source', 'assignedTo', 'priority', 'created'].includes(col.id),
      order: col.id === 'leadUid' ? 1 :
             col.id === 'name' ? 2 :
             col.id === 'contact' ? 3 :
             col.id === 'company' ? 4 :
             col.id === 'segments' ? 5 :
             col.id === 'stage' ? 6 :
             col.id === 'source' ? 7 :
             col.id === 'assignedTo' ? 8 :
             col.id === 'leadOwner' ? 9 :
             col.id === 'priority' ? 10 :
             col.id === 'created' ? 11 :
             col.id === 'actions' ? 12 :
             col.id === 'email' ? 13 :
             col.id === 'phone' ? 14 :
             col.id === 'leadValue' ? 15 :
             col.id === 'followUpDate' ? 16 :
             col.id === 'lastActivity' ? 17 :
             col.id === 'notes' ? 18 : col.order
    }));
    setColumns(defaultColumns);
    await saveColumnSettings(defaultColumns);
  };

  const getVisibleColumns = () => {
    return columns.filter(col => col.visible).sort((a, b) => a.order - b.order);
  };

  // Load column and table settings from backend
  const loadUserSettings = async () => {
    try {
      const userProfile = await userAPI.getProfile();
     
      
      // Load column settings
      const savedColumns = userProfile.preferences?.columnSettings?.leads;
      
      if (savedColumns && Array.isArray(savedColumns)) {
        // Merge saved columns with default columns to ensure new columns are included
        const defaultCols = [
          { id: 'leadUid', label: 'Lead ID', visible: true, order: 1 },
          { id: 'name', label: 'Name', visible: true, order: 2 },
          { id: 'contact', label: 'Contact', visible: true, order: 3 },
          { id: 'company', label: 'Company', visible: false, order: 4 },
          { id: 'segments', label: 'Segments', visible: true, order: 5 },
          { id: 'stage', label: 'Stage', visible: true, order: 6 },
          { id: 'source', label: 'Source', visible: true, order: 7 },
          { id: 'assignedTo', label: 'Assigned To', visible: true, order: 8 },
          { id: 'leadOwner', label: 'Lead Owner', visible: false, order: 9 },
          { id: 'priority', label: 'Priority', visible: true, order: 10 },
          { id: 'created', label: 'Created', visible: true, order: 11 },
          { id: 'email', label: 'Email', visible: false, order: 12 },
          { id: 'phone', label: 'Phone', visible: false, order: 13 },
          { id: 'leadValue', label: 'Lead Value', visible: false, order: 14 },
          { id: 'followUpDate', label: 'Follow-up Date', visible: false, order: 15 },
          { id: 'lastActivity', label: 'Last Activity', visible: false, order: 16 },
          { id: 'notes', label: 'Notes', visible: false, order: 17 }
        ];
        
        // Find columns that exist in default but not in saved (new columns)
        const savedColumnIds = savedColumns.map((col: ColumnConfig) => col.id);
        const newColumns = defaultCols.filter(col => !savedColumnIds.includes(col.id));
        
        // Merge: keep saved settings for existing columns, add new columns
        const mergedColumns = [...savedColumns, ...newColumns];
        setColumns(mergedColumns);
      } else {
        // No saved settings, use defaults
      }

      // Leads list always defaults to created date (newest first); we do not restore saved sort
      // so the list is consistently "created date wise" when opened. Users can still sort by
      // other columns during the session via the column headers.
    } catch (error) {
      console.error('Failed to load user settings:', error);
      // Keep default settings if loading fails
    }
  };

  // Save column settings to backend
  const saveColumnSettings = async (columnsToSave: ColumnConfig[]) => {
    try {
      setSavingColumnSettings(true);
      const response = await userAPI.updateColumnSettings(columnsToSave);
    } catch (error) {
      console.error('Failed to save column settings:', error);
      // Could show a toast notification here
    } finally {
      setSavingColumnSettings(false);
    }
  };

  // Save table settings to backend
  const saveTableSettings = async (sortByField: string, sortOrderField: 'ASC' | 'DESC') => {
    try {
      const response = await userAPI.updateTableSettings('leads', {
        sortBy: sortByField,
        sortOrder: sortOrderField
      });
    } catch (error) {
      console.error('Failed to save table settings:', error);
      // Could show a toast notification here
    }
  };

  // Drag and drop functions
  const handleDragStart = (e: React.DragEvent, columnId: string) => {
    setDraggedColumn(columnId);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent, columnId: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverColumn(columnId);
  };

  const handleDragLeave = () => {
    setDragOverColumn(null);
  };

  const handleDrop = async (e: React.DragEvent, targetColumnId: string) => {
    e.preventDefault();
    
    if (!draggedColumn || draggedColumn === targetColumnId) {
      setDraggedColumn(null);
      setDragOverColumn(null);
      return;
    }

    // Reorder columns
    const newColumns = [...columns];
    const draggedIndex = newColumns.findIndex(col => col.id === draggedColumn);
    const targetIndex = newColumns.findIndex(col => col.id === targetColumnId);
    
    if (draggedIndex === -1 || targetIndex === -1) {
      setDraggedColumn(null);
      setDragOverColumn(null);
      return;
    }
    
    // Remove dragged column
    const [draggedCol] = newColumns.splice(draggedIndex, 1);
    
    // Insert at target position
    newColumns.splice(targetIndex, 0, draggedCol);
    
    // Update order values
    const reorderedColumns = newColumns.map((col, index) => ({
      ...col,
      order: index + 1
    }));

    setColumns(reorderedColumns);
    await saveColumnSettings(reorderedColumns);

    setDraggedColumn(null);
    setDragOverColumn(null);
  };

  const handleDragEnd = () => {
    setDraggedColumn(null);
    setDragOverColumn(null);
  };

  const renderCellContent = (lead: Lead, column: ColumnConfig) => {
    switch (column.id) {
      case 'leadUid':
        return (
          <span className="text-sm font-mono font-medium text-blue-600">
            {lead.leadUid || '-'}
          </span>
        );
      case 'name':
        return (
          <div>
            <div className="text-sm font-medium text-gray-900">
              {lead.fullName}
            </div>
            {lead.jobTitle && (
              <div className="text-sm text-gray-500">{lead.jobTitle}</div>
            )}
          </div>
        );
      case 'contact':
        return (
          <div>
            <div className="text-sm text-gray-900">{lead.email}</div>
            {lead.phone && (
              <div className="text-sm text-gray-500">{lead.phone}</div>
            )}
          </div>
        );
      case 'company':
        return <span className="text-sm text-gray-900">{lead.company || '-'}</span>;
      case 'segments':
        if (!lead.segments || lead.segments.length === 0) {
          return <span className="text-gray-400 italic text-sm">No segment</span>;
        }
        return (
          <div className="flex flex-wrap gap-1">
            {lead.segments.slice(0, 2).map((segment) => (
              <span
                key={segment.id}
                className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium text-white"
                style={{ backgroundColor: segment.color }}
                title={segment.name}
              >
                {segment.code}
              </span>
            ))}
            {lead.segments.length > 2 && (
              <span 
                className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-200 text-gray-700"
                title={lead.segments.slice(2).map(s => s.name).join(', ')}
              >
                +{lead.segments.length - 2}
              </span>
            )}
          </div>
        );
      case 'stage':
        return lead.stage_name ? (
          <span 
            className="inline-flex px-2 py-1 text-xs font-semibold rounded-full text-white"
            style={{ backgroundColor: lead.stage_color }}
          >
            {lead.stage_name}
          </span>
        ) : '-';
      case 'source':
        return <span className="text-sm text-gray-900">{lead.source_name || '-'}</span>;
      case 'assignedTo':
        return lead.assigned_first_name && lead.assigned_last_name 
          ? <span className="text-sm text-gray-900">{lead.assigned_first_name} {lead.assigned_last_name}</span>
          : <span className="text-gray-400 italic">Unassigned</span>;
      case 'leadOwner':
        return lead.lead_owner_first_name && lead.lead_owner_last_name 
          ? <span className="text-sm text-gray-900">{lead.lead_owner_first_name} {lead.lead_owner_last_name}</span>
          : <span className="text-gray-400 italic">No Owner</span>;
      case 'priority':
        return (
          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getPriorityColor(lead.priority)}`}>
            {lead.priority.charAt(0).toUpperCase() + lead.priority.slice(1)}
          </span>
        );
      case 'created':
        return <span className="text-sm text-gray-500">{formatDateForDisplay(lead.createdAt)}</span>;
      case 'email':
        return <span className="text-sm text-gray-900">{lead.email}</span>;
      case 'phone':
        return <span className="text-sm text-gray-900">{lead.phone || '-'}</span>;
      case 'leadValue':
        return <span className="text-sm text-gray-900">{lead.leadValue ? `$${lead.leadValue.toLocaleString()}` : '-'}</span>;
      case 'followUpDate':
        return <span className="text-sm text-gray-900">{lead.followUpDate ? formatDateForDisplay(lead.followUpDate) : '-'}</span>;
      case 'lastActivity':
        return <span className="text-sm text-gray-900">{formatDateForDisplay(lead.updatedAt)}</span>;
      case 'notes':
        return <span className="text-sm text-gray-900">{lead.notes ? (lead.notes.length > 50 ? lead.notes.substring(0, 50) + '...' : lead.notes) : '-'}</span>;
      default:
        return '-';
    }
  };

  // Filters
  const [search, setSearch] = useState('');
  const [selectedStage, setSelectedStage] = useState<string>('');
  const [selectedSource, setSelectedSource] = useState<string>('');
  const [selectedAssigned, setSelectedAssigned] = useState<string>('');
  const [selectedPriority, setSelectedPriority] = useState<string>('');
  const [selectedSegments, setSelectedSegments] = useState<number[]>([]);  // NEW
  const [segmentFilterLogic, setSegmentFilterLogic] = useState<'AND' | 'OR'>('OR');  // NEW
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState<'ASC' | 'DESC'>('DESC');
  // Restore page when returning from Lead Details (so we don't reset to page 1)
  const [currentPage, setCurrentPage] = useState(() => {
    try {
      if (sessionStorage.getItem(LEADS_LIST_SCROLL_KEY) || sessionStorage.getItem(LEADS_LIST_RETURN_LEAD_ID_KEY)) {
        const p = sessionStorage.getItem(LEADS_LIST_PAGE_KEY);
        if (p) {
          const num = parseInt(p, 10);
          if (!isNaN(num) && num >= 1) return num;
        }
      }
    } catch (_) {}
    return 1;
  });

  // Column to sortable field mapping
  const sortableFields: { [key: string]: string } = {
    'name': 'full_name',
    'contact': 'email',
    'company': 'company',
    'stage': 'stage_name',
    'source': 'source_name',
    'assignedTo': 'assigned_name',
    'leadOwner': 'lead_owner_name',
    'priority': 'priority',
    'created': 'created_at',
    'email': 'email',
    'phone': 'phone',
    'leadValue': 'lead_value',
    'followUpDate': 'follow_up_date',
    'lastActivity': 'updated_at',
    'notes': 'notes'
  };

  // Fetch leads with current filters
  const fetchLeads = useCallback(async () => {
    // Don't fetch if branches are still loading
    if (branchLoading) {
      return;
    }
    
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: '20',
        sortBy,
        sortOrder
      });

      if (search) params.append('search', search);
      if (selectedStage) params.append('stageId', selectedStage);
      if (selectedSource) params.append('sourceId', selectedSource);
      if (selectedAssigned) params.append('assignedTo', selectedAssigned);
      if (selectedPriority) params.append('priority', selectedPriority);
      
      // Add segment filtering
      if (selectedSegments.length > 0) {
        params.append('segmentIds', selectedSegments.join(','));
        params.append('segmentLogic', segmentFilterLogic);
      }

      const response = await api.get(`/leads?${params.toString()}`);
      
      if (response.data.success) {
        setLeads(response.data.data);
        setPagination(response.data.pagination);
      } else {
        setError('Failed to fetch leads');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error fetching leads');
    } finally {
      setLoading(false);
    }
  }, [currentPage, search, selectedStage, selectedSource, selectedAssigned, selectedPriority, selectedSegments, segmentFilterLogic, sortBy, sortOrder, currentBranch, branchLoading]);


  // Fetch filter options
  const fetchFilterOptions = async () => {
    // Don't fetch if branches are still loading
    if (branchLoading) {
      return;
    }
    
    try {
      const [stagesRes, sourcesRes, usersRes, segmentsRes] = await Promise.all([
        api.get('/leads/stages'),
        api.get('/leads/sources'),
        api.get('/invitations/members'), // Get organization users
        customerSegmentApi.getSegments(false) // Get active segments
      ]);

   

      // Handle stages response
      if (stagesRes.data && stagesRes.data.success && stagesRes.data.data) {
        setStages(stagesRes.data.data);
      } else if (Array.isArray(stagesRes.data)) {
        setStages(stagesRes.data);
      }
      
      // Handle sources response
      if (sourcesRes.data && sourcesRes.data.success && sourcesRes.data.data) {
        setSources(sourcesRes.data.data);
      } else if (Array.isArray(sourcesRes.data)) {
        setSources(sourcesRes.data);
      }
      
      // Handle segments response
      if (segmentsRes && segmentsRes.success) {
        setSegments(segmentsRes.data || []);
      }

      // Handle users response - extract from members.users structure
      if (usersRes.data && usersRes.data.success && usersRes.data.members) {
        setUsers(usersRes.data.members.users || []);
      } else if (usersRes.data && Array.isArray(usersRes.data)) {
        setUsers(usersRes.data);
      }
    } catch (err) {
      console.error('Error fetching filter options:', err);
    }
  };

  useEffect(() => {
    if (!branchLoading) {
      fetchFilterOptions();
      loadUserSettings();
    }
  }, [branchLoading]);

  useEffect(() => {
    fetchLeads();
  }, [fetchLeads]);

  // Restore scroll position and highlight when returning from Lead Details
  // Clear keys after a delay so a second mount (e.g. React Strict Mode) still sees them for page init
  useEffect(() => {
    if (loading) return;
    try {
      const savedScroll = sessionStorage.getItem(LEADS_LIST_SCROLL_KEY);
      const returnLeadIdRaw = sessionStorage.getItem(LEADS_LIST_RETURN_LEAD_ID_KEY);
      if (savedScroll !== null) {
        const y = parseInt(savedScroll, 10);
        if (!isNaN(y)) {
          requestAnimationFrame(() => {
            window.scrollTo({ top: y, behavior: 'auto' });
          });
        }
      }
      if (returnLeadIdRaw !== null) {
        const id = parseInt(returnLeadIdRaw, 10);
        if (!isNaN(id)) {
          setHighlightedLeadId(id);
          const row = document.querySelector(`tr[data-lead-id="${id}"]`);
          if (row) {
            requestAnimationFrame(() => {
              row.scrollIntoView({ behavior: 'auto', block: 'nearest' });
            });
          }
        }
      }
      const clearKeys = () => {
        sessionStorage.removeItem(LEADS_LIST_SCROLL_KEY);
        sessionStorage.removeItem(LEADS_LIST_RETURN_LEAD_ID_KEY);
        sessionStorage.removeItem(LEADS_LIST_PAGE_KEY);
      };
      const t = setTimeout(clearKeys, 600);
      return () => clearTimeout(t);
    } catch (_) {}
  }, [loading]);

  // Clear highlight after a few seconds
  useEffect(() => {
    if (highlightedLeadId === null) return;
    const t = setTimeout(() => setHighlightedLeadId(null), 3000);
    return () => clearTimeout(t);
  }, [highlightedLeadId]);

  // Reset pagination when filters change (skip initial mount so we don't overwrite restored page from "Back to Leads")
  const isInitialMount = React.useRef(true);
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    setCurrentPage(1);
  }, [search, selectedStage, selectedSource, selectedAssigned, selectedPriority, selectedSegments]);

  // Handle sort
  const handleSort = async (columnId: string) => {
    const field = sortableFields[columnId];
    if (!field) return; // Column is not sortable
    
    let newSortBy = field;
    let newSortOrder: 'ASC' | 'DESC' = 'DESC';
    
    if (sortBy === field) {
      newSortOrder = sortOrder === 'ASC' ? 'DESC' : 'ASC';
    }
    
    setSortBy(newSortBy);
    setSortOrder(newSortOrder);
    
    // Save sorting preferences
    await saveTableSettings(newSortBy, newSortOrder);
  };

  // Check if a column is currently being sorted
  const isColumnSorted = (columnId: string) => {
    const field = sortableFields[columnId];
    return field && sortBy === field;
  };

  // Get sort direction for a column
  const getSortDirection = (columnId: string) => {
    const field = sortableFields[columnId];
    return field && sortBy === field ? sortOrder : null;
  };

  // Render sort indicator
  const renderSortIndicator = (columnId: string) => {
    if (!sortableFields[columnId]) return null;
    
    const direction = getSortDirection(columnId);
    if (!direction) return null;

    return (
      <div className="flex flex-col ml-1">
        <ChevronUpIcon 
          className={`w-3 h-3 ${direction === 'ASC' ? 'text-blue-600' : 'text-gray-400'}`} 
        />
        <ChevronDownIcon 
          className={`w-3 h-3 -mt-1 ${direction === 'DESC' ? 'text-blue-600' : 'text-gray-400'}`} 
        />
      </div>
    );
  };

  // Clear filters
  const clearFilters = () => {
    setSearch('');
    setSelectedStage('');
    setSelectedSource('');
    setSelectedAssigned('');
    setSelectedPriority('');
    setSelectedSegments([]);  // NEW: Clear segment filter
    setCurrentPage(1);
  };


  // Get priority badge color
  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent': return 'bg-red-100 text-red-800';
      case 'high': return 'bg-orange-100 text-orange-800';
      case 'medium': return 'bg-yellow-100 text-yellow-800';
      case 'low': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (error) {
    return (
      <Layout title="Leads" subtitle="Manage your leads and prospects">
        <div className="text-center py-12">
          <div className="text-red-500 mb-4">
            <svg className="mx-auto h-12 w-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <p className="text-gray-600">{error}</p>
          <button 
            onClick={() => window.location.reload()} 
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Retry
          </button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout title="Leads" subtitle="Manage your leads and prospects">
      <div className="space-y-6">
        {/* Branch Context Indicator */}
        {(currentBranch || isViewingAllBranches) && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-2 flex items-center space-x-2">
            <BuildingOfficeIcon className="h-5 w-5 text-blue-600" />
            <span className="text-sm font-medium text-blue-900">
              {isViewingAllBranches ? 'Viewing leads from all branches' : `Branch: ${currentBranch?.name}`}
            </span>
          </div>
        )}
        
        {/* Header Actions */}
        <div className="flex justify-between items-center">
          <div className="flex items-center space-x-4">
            <h2 className="text-lg font-medium text-gray-900">
              {pagination.totalRecords} Lead{pagination.totalRecords !== 1 ? 's' : ''}
            </h2>
            {(search || selectedStage || selectedSource || selectedAssigned || selectedPriority || selectedSegments.length > 0) && (
              <button
                onClick={clearFilters}
                className="text-sm text-gray-500 hover:text-gray-700 underline"
              >
                Clear filters
              </button>
            )}
          </div>
          <div className="flex space-x-3">
            <button
              onClick={() => navigate('/leads/pipeline')}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
              title="Switch to Pipeline View"
            >
              <svg className="w-4 h-4 inline mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2" />
              </svg>
              Pipeline
            </button>
            <button
              onClick={() => setShowColumnSettings(true)}
              className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
            >
              <Cog6ToothIcon className="w-4 h-4 inline mr-2" />
              Columns
            </button>
            <PermissionGate module="leads" permission="create">
              <button 
                onClick={() => setShowBulkImportModal(true)}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                <ArrowUpTrayIcon className="w-4 h-4 inline mr-2" />
                Bulk Import
              </button>
            </PermissionGate>
            <PermissionGate module="leads" permission="create">
              <button 
                onClick={() => navigate('/leads/new')}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <svg className="w-4 h-4 inline mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Add Lead
              </button>
            </PermissionGate>
          </div>
        </div>

        {/* Modern Filter Bar */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          {/* Active Filters - Beautiful Badge Display */}
          {(selectedSegments.length > 0 || search || selectedStage || selectedSource || selectedAssigned || selectedPriority) && (
            <div className="px-5 py-3 bg-gradient-to-r from-blue-50 via-purple-50 to-pink-50 border-b border-gray-200">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center flex-wrap gap-2">
                  {selectedSegments.map(segId => {
                    const segment = segments.find(s => s.id === segId);
                    if (!segment) return null;
                    return (
                      <span key={segId} className="inline-flex items-center pl-2.5 pr-1.5 py-1 rounded-full text-xs font-medium text-white shadow-sm" style={{ backgroundColor: segment.color }}>
                        {segment.name}
                        <button type="button" onClick={() => setSelectedSegments(selectedSegments.filter(id => id !== segId))} className="ml-1.5 inline-flex items-center justify-center w-4 h-4 rounded-full hover:bg-white hover:bg-opacity-25">×</button>
                      </span>
                    );
                  })}
                  {search && <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-slate-700 text-white">🔍 {search.substring(0, 15)}{search.length > 15 && '...'}<button onClick={() => setSearch('')} className="ml-1.5">×</button></span>}
                  {selectedStage && <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-blue-600 text-white">{stages.find(s => s.id === parseInt(selectedStage))?.name}<button onClick={() => setSelectedStage('')} className="ml-1.5">×</button></span>}
                  {selectedSource && <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-emerald-600 text-white">{sources.find(s => s.id === parseInt(selectedSource))?.name}<button onClick={() => setSelectedSource('')} className="ml-1.5">×</button></span>}
                  {selectedPriority && <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-amber-600 text-white">{selectedPriority.toUpperCase()}<button onClick={() => setSelectedPriority('')} className="ml-1.5">×</button></span>}
                  {selectedAssigned && <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-indigo-600 text-white">{selectedAssigned === 'unassigned' ? 'Unassigned' : users.find(u => u.id === parseInt(selectedAssigned))?.fullName}<button onClick={() => setSelectedAssigned('')} className="ml-1.5">×</button></span>}
                </div>
                <button onClick={clearFilters} className="text-xs font-semibold text-gray-600 hover:text-gray-900 px-3 py-1 rounded-md hover:bg-white transition-colors">Clear all</button>
              </div>
            </div>
          )}

          {/* Filter Controls */}
          <div className="p-4">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-3">
              {/* Search */}
              <div className="lg:col-span-4">
                <div className="relative">
                  <svg className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
                  <input type="text" placeholder="Search by name, email, company..." value={search} onChange={(e) => setSearch(e.target.value)} className="w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm placeholder-gray-400" />
                </div>
              </div>

              {/* Filters */}
              {segments.length > 0 && (
                <div className="lg:col-span-2">
                  <select value="" onChange={(e) => { const id = parseInt(e.target.value); if (id && !selectedSegments.includes(id)) setSelectedSegments([...selectedSegments, id]); }} className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors">
                    <option value="">{selectedSegments.length === 0 ? 'Segment' : `Segment (${selectedSegments.length})`}</option>
                    {segments.filter(seg => !selectedSegments.includes(seg.id)).map(segment => (<option key={segment.id} value={segment.id}>{segment.name}</option>))}
                  </select>
                </div>
              )}
              
              <div className={segments.length > 0 ? 'lg:col-span-2' : 'lg:col-span-2'}>
                <select value={selectedStage} onChange={(e) => setSelectedStage(e.target.value)} className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm font-medium text-gray-700 bg-white hover:bg-gray-50">
                  <option value="">Stage</option>
                  {stages.map(stage => (<option key={stage.id} value={stage.id}>{stage.name}</option>))}
                </select>
              </div>
              
              <div className="lg:col-span-2">
                <select value={selectedSource} onChange={(e) => setSelectedSource(e.target.value)} className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent text-sm font-medium text-gray-700 bg-white hover:bg-gray-50">
                  <option value="">Source</option>
                  {sources.map(source => (<option key={source.id} value={source.id}>{source.name}</option>))}
                </select>
              </div>
              
              <div className="lg:col-span-1">
                <select value={selectedAssigned} onChange={(e) => setSelectedAssigned(e.target.value)} className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm font-medium text-gray-700 bg-white hover:bg-gray-50">
                  <option value="">Assigned</option>
                  <option value="unassigned">Unassigned</option>
                  {users.map(user => (<option key={user.id} value={user.id}>{user.fullName}</option>))}
                </select>
              </div>
              
              <div className="lg:col-span-1">
                <select value={selectedPriority} onChange={(e) => setSelectedPriority(e.target.value)} className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm font-medium text-gray-700 bg-white hover:bg-gray-50">
                  <option value="">Priority</option>
                  <option value="urgent">Urgent</option>
                  <option value="high">High</option>
                  <option value="medium">Medium</option>
                  <option value="low">Low</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Leads Table */}
        <div className="bg-white rounded-lg shadow border overflow-hidden">
          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <p className="mt-2 text-gray-600">Loading leads...</p>
            </div>
          ) : leads.length === 0 ? (
            <div className="text-center py-12">
              <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
              <h3 className="mt-2 text-sm font-medium text-gray-900">No leads found</h3>
              <p className="mt-1 text-sm text-gray-500">Get started by creating your first lead.</p>
              <div className="mt-6">
                <PermissionGate module="leads" permission="create">
                  <button 
                    onClick={() => navigate('/leads/new')}
                    className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                  >
                    <svg className="-ml-1 mr-2 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    Add Lead
                  </button>
                </PermissionGate>
              </div>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      {getVisibleColumns().map((column) => (
                        <th 
                          key={column.id}
                          onClick={sortableFields[column.id] ? () => handleSort(column.id) : undefined}
                          className={`px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider ${
                            sortableFields[column.id] ? 'cursor-pointer hover:bg-gray-100 select-none' : ''
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <span>{column.label}</span>
                            {renderSortIndicator(column.id)}
                          </div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {leads.map((lead) => (
                      <tr 
                        key={lead.id}
                        data-lead-id={lead.id}
                        className={`hover:bg-gray-50 cursor-pointer ${highlightedLeadId === lead.id ? 'bg-blue-50 ring-1 ring-blue-200 ring-inset' : ''}`}
                        onClick={() => {
                      try {
                        sessionStorage.setItem(LEADS_LIST_SCROLL_KEY, String(window.scrollY));
                        sessionStorage.setItem(LEADS_LIST_RETURN_LEAD_ID_KEY, String(lead.id));
                        sessionStorage.setItem(LEADS_LIST_PAGE_KEY, String(currentPage));
                        sessionStorage.setItem('leadshive_last_page', window.location.pathname + window.location.search);
                      } catch (_) {}
                      navigate(`/leads/${lead.id}`);
                    }}
                      >
                        {getVisibleColumns().map((column) => (
                          <td 
                            key={column.id}
                            className="px-6 py-4 whitespace-nowrap"
                          >
                            {renderCellContent(lead, column)}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {pagination.totalPages > 1 && (
                <div className="bg-white px-6 py-3 flex items-center justify-between border-t border-gray-200">
                  <div className="flex-1 flex justify-between sm:hidden">
                    <button
                      onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                      disabled={!pagination.hasPrevPage}
                      className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                    >
                      Previous
                    </button>
                    <button
                      onClick={() => setCurrentPage(Math.min(pagination.totalPages, currentPage + 1))}
                      disabled={!pagination.hasNextPage}
                      className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                    >
                      Next
                    </button>
                  </div>
                  <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                    <div>
                      <p className="text-sm text-gray-700">
                        Showing <span className="font-medium">{((currentPage - 1) * 20) + 1}</span> to{' '}
                        <span className="font-medium">
                          {Math.min(currentPage * 20, pagination.totalRecords)}
                        </span> of{' '}
                        <span className="font-medium">{pagination.totalRecords}</span> results
                      </p>
                    </div>
                    <div>
                      <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                        <button
                          onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                          disabled={!pagination.hasPrevPage}
                          className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                        >
                          <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                          </svg>
                        </button>
                        
                        {/* Page numbers */}
                        {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                          const pageNum = i + 1;
                          return (
                            <button
                              key={pageNum}
                              onClick={() => setCurrentPage(pageNum)}
                              className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                                currentPage === pageNum
                                  ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                                  : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                              }`}
                            >
                              {pageNum}
                            </button>
                          );
                        })}
                        
                        <button
                          onClick={() => setCurrentPage(Math.min(pagination.totalPages, currentPage + 1))}
                          disabled={!pagination.hasNextPage}
                          className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                        >
                          <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </button>
                      </nav>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Column Settings Modal */}
      {showColumnSettings && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">Column Settings</h3>
                <button
                  onClick={() => setShowColumnSettings(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="w-6 h-6" />
                </button>
              </div>
              
              <p className="text-sm text-gray-600 mb-4">
                Drag and drop to reorder columns. Check/uncheck to show/hide columns.
              </p>
              
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {columns.sort((a, b) => a.order - b.order).map((column) => (
                  <div 
                    key={column.id}
                    draggable
                    onDragStart={(e) => handleDragStart(e, column.id)}
                    onDragOver={(e) => handleDragOver(e, column.id)}
                    onDragLeave={handleDragLeave}
                    onDrop={(e) => handleDrop(e, column.id)}
                    onDragEnd={handleDragEnd}
                    className={`flex items-center justify-between p-3 rounded-lg border-2 transition-all duration-200 ${
                      draggedColumn === column.id 
                        ? 'opacity-50 bg-blue-50 border-blue-300' 
                        : dragOverColumn === column.id 
                        ? 'bg-blue-50 border-blue-300 border-dashed' 
                        : 'bg-white border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-center space-x-3">
                      <div className="cursor-move text-gray-400 hover:text-gray-600">
                        <Bars3Icon className="w-5 h-5" />
                      </div>
                      <label className="text-sm font-medium text-gray-700 cursor-pointer">
                        {column.label}
                      </label>
                    </div>
                    <input
                      type="checkbox"
                      checked={column.visible}
                      onChange={() => toggleColumnVisibility(column.id)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                  </div>
                ))}
              </div>
              
              <div className="flex justify-between mt-6">
                <button
                  onClick={resetToDefaultColumns}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                >
                  Reset to Default
                </button>
                <button
                  onClick={() => setShowColumnSettings(false)}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Done
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Lead Import Modal */}
      <BulkLeadImportModal
        isOpen={showBulkImportModal}
        onClose={() => setShowBulkImportModal(false)}
        onSuccess={() => {
          setShowBulkImportModal(false);
          fetchLeads();
        }}
      />
      
    </Layout>
  );
};

export default Leads;
